/* BackupSummaryRenderer.js */

"use strict";

const ButtonControl = require("@gluenetworks/steve_ui_core").Control.Action.Button;
const _ = require("lodash");

class BackupSummaryRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("backup-summary-gl");
        this._rootElem.component = this._model;
        this._logger = this._model.logger;
        this._createElements();
        this._registerListeners();
    }

    _createElements() {
        this._titlePanel = this._createPanel("h2", "summary-title");
        this._summaryPanel = this._createPanel("div", "summary");
        this._errorPanel = this._createPanel("div", "backup-errors");
        this._errorLabel = this._createPanel("div", "error-label");
        this._errorLabel.innerHTML = "Errors:";
        this._errorList = this._createPanel("div", "error-list");
        this._fillerPanel = this._createPanel("div", "filler");
        this._buttonBar = this._createPanel("div", "summary-buttons");
        this._okButton = new ButtonControl({
            label: "OK",
            darkTheme: true,
        });
    }

    _createPanel(elementType, className) {
        const panel = document.createElement(elementType);
        panel.classList.add(className);
        return panel;
    }

    _registerListeners() {
        this._okButton.on("UserClick", this._onOkClick.bind(this));
    }

    async shutdown() {
        this._unregisterListeners();
        await this._okButton.shutdown();
        this._summaryPanel = undefined;
        this._errorPanel = undefined;
    }

    render() {
        return new Promise((resolve) => {
            if (!this._rootElem.hasChildNodes()) {
                this._appendChildren();
                this._displaySummary();
            }
            resolve();
        });
    }

    _appendChildren() {
        this._rootElem.appendChild(this._titlePanel);
        this._rootElem.appendChild(this._summaryPanel);
        this._rootElem.appendChild(this._errorLabel);
        this._rootElem.appendChild(this._errorPanel);
        this._rootElem.appendChild(this._fillerPanel);
        this._rootElem.appendChild(this._buttonBar);
        this._buttonBar.appendChild(this._okButton.renderer.rootElement);
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    _unregisterListeners() {
        this._okButton.removeAllListeners("UserClick");
    }

    _onOkClick() {
        this._model.sendOk();
    }

    _setTitle() {
        let title = "Backup Completed";
        const firstError = _.get(this.model.backupResults.summary, "firstError", "");

        if (firstError) {
            title += firstError.startsWith("ExceededDeviceCount")
                ? ": Device license count has been exceeded"
                : ": Errors found";
            this._titlePanel.classList.add("error-title");
        } else {
            this._titlePanel.classList.remove("error-title");
        }

        this._titlePanel.innerHTML = title;
    }

    _displaySummary() {
        this._setTitle();
        this._clearChildren(this._summaryPanel);

        const {targetCount, successCount, failureCount} = this._model.backupResults.summary;

        this._summaryPanel.appendChild(
            this._createSpan(`Total number of devices: ${targetCount || 0}`)
        );
        this._summaryPanel.appendChild(
            this._createSpan(`Backups successful: ${successCount || 0}`)
        );
        this._summaryPanel.appendChild(this._createSpan(`Backups in error: ${failureCount || 0}`));

        this._summaryPanel.appendChild(document.createElement("br"));

        this._clearChildren(this._errorList);

        if (_.isNumber(failureCount) && failureCount > 0) {
            this._errorLabel.classList.remove("hidden");
            this._errorPanel.classList.remove("hidden");
            this._fillerPanel.classList.add("hidden");

            const backupResults = this._model.backupResults;

            for (const targetKey in backupResults.targetErrors) {
                const errorMsg = this._createPanel("div", "error-msg");
                errorMsg.innerHTML = backupResults.targetErrors[targetKey];
                this._errorList.appendChild(errorMsg);
            }
            this._errorPanel.appendChild(this._errorList);
        } else {
            this._errorLabel.classList.add("hidden");
            this._errorPanel.classList.add("hidden");
            this._fillerPanel.classList.remove("hidden");
        }
    }

    _clearChildren(element) {
        while (element.firstChild) {
            element.firstChild.remove();
        }
    }

    _createSpan(text) {
        const span = document.createElement("span");
        span.innerText = text;
        return span;
    }
}

module.exports = BackupSummaryRenderer;
